import { Header, notify } from "@general-backoffice/core/index";
import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Alert, Button, Col, Container, Row } from "reactstrap";

import { referralCodesEndpoints, referrerEndpoints } from "../../../../api";
import useClaimHistory from "../../../../components/payouts/hooks/useClaimHistory";
import PayoutsTableCard from "../../../../components/payouts/PayoutsTableCard";
import useReferrals from "../../../../components/referral/hooks/useReferrals";
import PartnerReferralsTableCard from "../../../../components/referral/PartnerReferralsTableCard";
import { ClaimFlowModal } from "../../../../components/referrer/claim";
import DiscountWalletCard from "../../../../components/referrer/DiscountWalletCard";
import useReferralCode from "../../../../components/referrer/hooks/useReferralCode";
import AmbassadorInfoModal from "../../../../components/referrer/partner/AmbassadorInfoModal";
import CreateReferralCodeModal from "../../../../components/referrer/partner/CreateReferralCodeModal";
import InfluencerInfoModal from "../../../../components/referrer/partner/InfluencerInfoModal";
import PartnerRecurringRequirementsCard from "../../../../components/referrer/partner/PartnerRecurringRequirementsCard";
import PayoutWalletCard from "../../../../components/referrer/partner/PayoutWalletCard";
import ReferralCodesTableCard from "../../../../components/referrer/partner/ReferralCodesTableCard";
import PartnerTnCModal from "../../../../components/referrer/partner/TnCModal";
import ReferralLinkCard from "../../../../components/referrer/ReferralLinkCard";
import generateReferrerLink from "../../../../components/referrer/utils/generateReferrerLink";
import useUser from "../../../../components/users/hooks/useUser";
import WarningBanner from "../../../../components/warnings/WarningBanner";
import useFetch from "../../../../hooks/useFetch";
import type { RootState } from "../../../../redux/reducers/session/state";
import isVipOrZeroPriceTier from "../../../../utils/isVipOrZeroPriceTier";
import refreshMe from "../../../../utils/refreshMe";


let hasShownPopUp = false;

const ReferrerPartner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard", { keyPrefix: "referrerPartner" });
  const state = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [showClaimFlow, setShowClaimFlow] = useState(false);

  const tncAccepted = state?.sessionState?.user?.hasAcceptedAffiliateTnC;
  const userBlocked = state?.sessionState?.user?.isBlockedFromAffiliate;
  const isVIP = isVipOrZeroPriceTier(state?.sessionState?.user);

  const referrerType = state?.sessionState?.user?.referrerType;

  const { state: discountWallet, isLoading: isLoadingDiscountWallet } = useFetch(() =>
    referrerEndpoints.getDiscountWallet().then(({ data }) => data)
  );

  const {
    state: payoutWallet,
    isLoading: isLoadingPayoutWallet,
    refresh: requestPayoutWallet,
  } = useFetch(() => referrerEndpoints.getPayoutsWallet().then(({ data }) => data));

  const {
    state: recurringRequirements,
    isLoading: isLoadingRecurringRequirements,
    // refresh: requestRequirementsToAmbassador
  } = useFetch(() => referrerEndpoints.getRecurringRequirements().then(({ data }) => data));

  const {
    referrals = [],
    isLoading: isLoadingReferrals,
    ...referralsProps
  } = useReferrals({
    pagination: { pageSize: 5 },
    filters: { pending: false, expired: false, claimed: false },
    mine: true,
  });

  const [isOpenInfoModal, setIsOpenInfoModal] = useState(false);

  const { state: referrerCode, isLoading: isLoadingReferrerCode } = useFetch(
    () => referrerEndpoints.getReferrerCode().then(({ data }) => data),
    { auto: tncAccepted }
  );
  const referrerLink = referrerCode ? generateReferrerLink(referrerCode) : undefined;

  const {
    state: referralCodesResponse,
    isLoading: isLoadingReferralCodes,
    refresh: requestReferralCodes,
  } = useFetch(() => referralCodesEndpoints.getMyReferralCodes().then(({ data }) => data));
  const referralCodes = referralCodesResponse ?? [];

  const { claims, refresh: refreshClaims, isLoading: isLoadingClaims } = useClaimHistory();

  const {
    referralCode: referralCodeToCreate,
    setReferralCode: setReferralCodeToCreate,
    isLoading: isLoadingReferralCodeToCreate,
    update: createReferralCodeToCreate,
  } = useReferralCode();

  const {
    pagination: { total: totalReferrals },
    isLoading: isLoadingTotalReferrals,
  } = useReferrals({
    fields: [],
    filters: { pending: false },
    pagination: { pageSize: 1 },
    mine: true,
  });

  // Open info modal on entering if has no referrals (only after first load completes)
  useEffect(() => {
    if (!hasShownPopUp && totalReferrals) hasShownPopUp = true;
    if (!hasShownPopUp && !isLoadingTotalReferrals && !totalReferrals) {
      setIsOpenInfoModal(true);
      hasShownPopUp = true;
    }
  }, [isLoadingTotalReferrals, totalReferrals]);

  const { user } = useUser("me", {
    params: {
      populate: ["taxProfile"],
      fields: [],
    },
  });

  const InfoModal = referrerType === "AMBASSADOR" ? AmbassadorInfoModal : InfluencerInfoModal;

  const isOpenTnCModal = !tncAccepted;
  return (
    <>
      <InfoModal isOpen={isOpenInfoModal && !isOpenTnCModal} close={() => setIsOpenInfoModal(false)} />

      <PartnerTnCModal
        isOpen={isOpenTnCModal}
        close={() => navigate("/")}
        acceptTerms={() => {
          referrerEndpoints
            .acceptTnC()
            .then(() => refreshMe({ state, dispatch }))
            .catch(() => notify.error(t("errorOnAcceptTnC")));
        }}
      />

      <CreateReferralCodeModal
        referralCode={referralCodeToCreate}
        close={() => setReferralCodeToCreate(null)}
        setReferralCode={setReferralCodeToCreate}
        isLoading={isLoadingReferralCodeToCreate}
        create={() => {
          createReferralCodeToCreate().then(() => {
            requestReferralCodes();
            setReferralCodeToCreate(null);
          });
        }}
      />

      {/* New Tax Compliance Claim Flow */}
      <ClaimFlowModal
        isOpen={showClaimFlow}
        onClose={() => setShowClaimFlow(false)}
        onSuccess={() => {
          setShowClaimFlow(false);
          requestPayoutWallet();
          refreshClaims();
        }}
        onRefresh={refreshClaims}
      />

      <Header.BreadCrumb
        color="secondary"
        isDark={false}
        buttons={[
          {
            children: <i className="fas fa-info-circle"></i>,
            color: "default",
            size: "md",
            className: "shadow-none position-absolute",
            style: { transform: "translateY(-50%)" },
            onClick: () => setIsOpenInfoModal(true),
          },
        ]}
      />
      <Container className="mt--6" fluid>
        <Row>
          <Col xs={12} xl={8}>
            {userBlocked && (
              <div style={{ marginBottom: 30 }}>
                <WarningBanner
                  title={t("userBlockedWarning.title")}
                  text={
                    <Trans
                      t={t}
                      i18nKey={"content"}
                      components={[
                        <a key="0" href="mailto:customer@investhinks.com">
                          customer@investhinks.com
                        </a>,
                      ]}
                    />
                  }
                  type={"danger"}
                />
              </div>
            )}
            {/* Partner Tax Profile Incomplete Warning */}
            {user && !user.taxProfile?.taxProfileComplete && (
              <Alert color="warning" className="mb-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="alert-heading mb-1">
                      <i className="fas fa-exclamation-triangle mr-2" />
                      {t("taxProfileIncomplete.title", "Complete Your Tax Profile")}
                    </h5>
                    <p className="mb-0">
                      {user.taxProfile
                        ? t(
                            "taxProfileIncomplete.textDeclarations",
                            "As a partner, you need to complete additional declarations in your tax profile before you can claim commissions."
                          )
                        : t(
                            "taxProfileIncomplete.textMissing",
                            "Please complete your tax profile to claim your commissions."
                          )}
                    </p>
                  </div>
                  <Button color="warning" size="sm" onClick={() => navigate("/settings/billing")}>
                    {t("taxProfileIncomplete.button", "Update Profile")}
                  </Button>
                </div>
              </Alert>
            )}
            <Row>
              <Col>
                <PayoutWalletCard
                  payout={payoutWallet}
                  isLoading={isLoadingPayoutWallet}
                  onClaim={() => {
                    if (user?.taxProfile) {
                      // User has tax profile - use new claim flow
                      setShowClaimFlow(true);
                    } else {
                      // No tax profile - redirect to billing settings
                      navigate("/settings/billing");
                    }
                  }}
                />
              </Col>
              {!!discountWallet?.availableRewards?.length && (
                <Col xs={12} md={6} className="h-100">
                  <DiscountWalletCard
                    amount={discountWallet?.balance}
                    unused={discountWallet?.availableRewards?.reduce((sum, r) => sum + (r.amount || 0), 0) ?? 0}
                    isLoading={isLoadingDiscountWallet}
                  />
                </Col>
              )}
            </Row>

            <PartnerReferralsTableCard
              {...referralsProps}
              totalReferrals={totalReferrals}
              isLoading={isLoadingReferrals}
              hideButtons={true}
              referrals={referrals}
              onUserClick={referrerType === "DISTRIBUTOR" ? (r) => navigate(`/users/${r.referee?.id}`) : undefined}
            />
          </Col>
          <Col xs={12} xl={4}>
            <ReferralLinkCard
              link={referrerLink}
              isLoading={isLoadingReferrerCode}
              title={t("referralLinkCard.title")}
              subtitle={t("referralLinkCard.subtitle")}
              onClickButton={async () => {
                // TODO: Fix type - referrerLink should always be string when button is clickable
                await navigator.clipboard.writeText(referrerLink ?? "");
                notify(t("referralLinkCard.messageLinkCopied"));
              }}
            />
            <ReferralCodesTableCard
              setReferralCodeToCreate={setReferralCodeToCreate}
              referralCodes={referralCodes}
              isLoading={isLoadingReferralCodes}
            />

            {!isVIP && (
              <PartnerRecurringRequirementsCard
                requirements={recurringRequirements?.requirements}
                isLoading={isLoadingRecurringRequirements}
              />
            )}
          </Col>
          <Col xl={12}>
            <PayoutsTableCard
              title={t("payoutsTableTitle")}
              claims={claims}
              filtersToShow={["downloadSelected"]}
              isLoading={isLoadingClaims}
              onClaimProcessed={refreshClaims}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ReferrerPartner;
